<template>
	<FullLayout>
		<template #topbar>
			<div class="topbar-content">
				<i class="bi bi-map me-2"></i>
				<span class="fw-semibold me-3">MapVision</span>
				<span class="ms-auto">
					<i
						class="bi bi-question-circle fs-5 text-muted"
						style="cursor: pointer"
						title="Help"
						@click="helpOpen = true"
					></i>
				</span>
			</div>
		</template>
		<mgl-map
			:map-style="mapStyleURL(ALIDADE_SMOOTH)"
			:center="[-105, 39]"
			:zoom="5"
			@map:load="onMapLoad"
		>
			<SourceServiceArea @service-area-loaded="onServiceAreaLoaded" />
			<SourceMapvisionZone />
			<SourceMapvisionSites :hidden-types="[...hiddenTypes]" />
			<SourceParcel />
		</mgl-map>

		<ToolRail
			side="left"
			v-model="leftTab"
			topOffset="60px"
			:tabs="[{ key: 'site', icon: 'bi bi-house', title: 'Site' }]"
		>
			<template #site>
				<div class="site-search">
					<input
						v-model="searchQuery"
						class="form-control form-control-sm"
						type="text"
						placeholder="Search site (name or id)"
						@input="onSearchInput"
						@focus="searchOpen = searchResults.length > 0"
						@blur="onSearchBlur"
					/>
					<ul
						v-if="searchOpen && searchResults.length"
						class="site-search-results"
					>
						<li
							v-for="r in searchResults"
							:key="r.id"
							class="site-search-item"
							@mousedown.prevent="selectSearchResult(r)"
						>
							<span class="site-search-name">{{
								r.name || `Site ${r.site_id}`
							}}</span>
							<span class="site-search-meta">
								#{{ r.site_id
								}}<template v-if="r.type_name"> · {{ r.type_name }}</template
								><template v-if="r.city"> · {{ r.city }}</template>
							</span>
						</li>
					</ul>
				</div>
				<div v-if="selectedSite" class="site-detail">
					<div class="site-detail-title">{{ siteTitle }}</div>
					<div
						v-for="[label, value] in siteRows"
						:key="label"
						class="site-detail-row"
					>
						<span class="site-detail-label">{{ label }}</span>
						<span class="site-detail-value">{{ value }}</span>
					</div>
					<div v-if="parcel" class="site-parcel">
						<div class="site-detail-title">Parcel</div>
						<div v-if="parcel.address_raw" class="site-detail-row">
							<span class="site-detail-label">Address</span>
							<span class="site-detail-value">{{ parcel.address_raw }}</span>
						</div>
						<div v-if="parcel.description" class="site-detail-row">
							<span class="site-detail-label">Description</span>
							<span class="site-detail-value">{{ parcel.description }}</span>
						</div>
						<div v-if="parcel.property_type" class="site-detail-row">
							<span class="site-detail-label">Property type</span>
							<span class="site-detail-value">{{ parcel.property_type }}</span>
						</div>
					</div>
					<div v-if="situs" class="site-situs">
						<div class="site-detail-title">Assessment</div>
						<div v-if="situs.address_raw" class="site-detail-row">
							<span class="site-detail-label">Address</span>
							<span class="site-detail-value">{{ situs.address_raw }}</span>
						</div>
						<div v-if="situs.lot_type" class="site-detail-row">
							<span class="site-detail-label">Lot type</span>
							<span class="site-detail-value">{{ situs.lot_type }}</span>
						</div>
						<div v-if="situs.property_type" class="site-detail-row">
							<span class="site-detail-label">Property type</span>
							<span class="site-detail-value">{{ situs.property_type }}</span>
						</div>
						<div v-if="situs.lot_size_acres != null" class="site-detail-row">
							<span class="site-detail-label">Lot size</span>
							<span class="site-detail-value">
								{{ situs.lot_size_acres }} ac
							</span>
						</div>
						<div v-if="situs.year_built != null" class="site-detail-row">
							<span class="site-detail-label">Year built</span>
							<span class="site-detail-value">{{ situs.year_built }}</span>
						</div>
						<div v-if="situs.bedrooms != null" class="site-detail-row">
							<span class="site-detail-label">Bedrooms</span>
							<span class="site-detail-value">{{ situs.bedrooms }}</span>
						</div>
						<div v-if="situs.building_sqft != null" class="site-detail-row">
							<span class="site-detail-label">Building area</span>
							<span class="site-detail-value">
								{{ fmtNumber(situs.building_sqft) }} sq ft
							</span>
						</div>
						<div
							v-if="situs.net_assessed_value != null"
							class="site-detail-row"
						>
							<span class="site-detail-label">Assessed value</span>
							<span class="site-detail-value">
								{{ fmtCurrency(situs.net_assessed_value) }}
							</span>
						</div>
						<div
							v-for="row in situsRawRows"
							:key="row.label"
							class="site-detail-row"
						>
							<span class="site-detail-label">{{ row.label }}</span>
							<span class="site-detail-value">{{ row.value }}</span>
						</div>
					</div>
					<div v-if="siteHistory.length" class="site-history">
						<div class="site-detail-title">History</div>
						<div class="site-history-scroll">
							<table class="table table-sm align-middle">
								<thead>
									<tr>
										<th>Valid</th>
										<th>Expired</th>
										<th>Type</th>
										<th>Sub-type</th>
										<th>Zone</th>
										<th>Lat</th>
										<th>Lon</th>
										<th>Del</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="h in siteHistory" :key="h.h_id">
										<td>{{ fmtDate(h.date_valid) }}</td>
										<td>{{ fmtDate(h.date_expired) }}</td>
										<td>{{ h.site_type }}</td>
										<td>{{ h.site_subtype }}</td>
										<td>{{ h.zone }}</td>
										<td>{{ h.latitude }}</td>
										<td>{{ h.longitude }}</td>
										<td>{{ fmtDeleted(h.is_deleted) }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</template>
		</ToolRail>

		<ToolRail
			side="right"
			v-model="rightTab"
			topOffset="60px"
			:tabs="[
				{
					key: 'site-types',
					icon: 'bi bi-funnel',
					title: 'Site Types',
					badgeCount: hiddenTypes.size,
				},
			]"
		>
			<template #site-types>
				<div class="type-filter-list">
					<label
						v-for="[type, symbol] in typeRows"
						:key="type"
						class="type-filter-row"
					>
						<input
							type="checkbox"
							class="form-check-input me-2"
							:checked="!hiddenTypes.has(type)"
							@change="toggleType(type)"
						/>
						<img class="type-filter-symbol" :src="symbol.url" :alt="type" />
						<span class="type-filter-name">{{ type }}</span>
					</label>
				</div>
			</template>
		</ToolRail>

		<MapvisionHelpModal v-model="helpOpen" />
	</FullLayout>
</template>

<style scoped>
.topbar-content {
	display: flex;
	align-items: center;
	background-color: rgba(255, 255, 255, 0.95);
	border-bottom: 1px solid #dee2e6;
	padding: 0.5rem 1rem;
}

.site-detail-title {
	font-weight: 600;
	font-size: 0.95rem;
	margin-bottom: 8px;
}

.site-detail-row {
	display: flex;
	justify-content: space-between;
	gap: 16px;
	padding: 3px 0;
	font-size: 0.85rem;
	border-bottom: 1px solid #f1f3f5;
}

.site-detail-label {
	color: #6c757d;
}

.site-search {
	position: relative;
	margin-bottom: 12px;
}

.site-search-results {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	max-height: 260px;
	overflow-y: auto;
	z-index: 1000;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
	list-style: none;
	margin: 2px 0 0;
	padding: 0;
}

.site-search-item {
	padding: 8px 10px;
	cursor: pointer;
	border-bottom: 1px solid #f1f3f5;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.site-search-item:last-child {
	border-bottom: none;
}

.site-search-item:hover {
	background: #f8f9fa;
}

.site-search-name {
	font-weight: 500;
	font-size: 0.85rem;
}

.site-search-meta {
	font-size: 0.75rem;
	color: #6c757d;
}

.site-detail-value {
	font-weight: 500;
	text-align: right;
}

.site-history {
	margin-top: 12px;
}

.site-parcel {
	margin-top: 12px;
}

.site-situs {
	margin-top: 12px;
}

.site-history-scroll {
	max-height: 240px;
	overflow: auto;
	border: 1px solid #dee2e6;
	border-radius: 4px;
}

.site-history-scroll :deep(table) {
	width: 100%;
	font-size: 0.75rem;
	white-space: nowrap;
	margin-bottom: 0;
}

/* Header styled like the page's muted site-detail-label, and kept visible
   while the scroll area moves. */
.site-history-scroll :deep(thead th) {
	color: #6c757d;
	font-weight: 500;
	font-size: 0.75rem;
	border-bottom: 1px solid #dee2e6;
	background: #fff;
	position: sticky;
	top: 0;
	z-index: 1;
}

/* Row separators match the site-detail rows above. */
.site-history-scroll :deep(tbody tr) {
	border-bottom: 1px solid #f1f3f5;
}

.site-history-scroll :deep(th),
.site-history-scroll :deep(td) {
	padding: 3px 8px;
	vertical-align: top;
}

.type-filter-row {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 0;
	cursor: pointer;
	user-select: none;
}

.type-filter-symbol {
	height: 20px;
	width: auto;
	max-width: 32px;
	object-fit: contain;
	flex-shrink: 0;
}

.type-filter-name {
	font-size: 0.85rem;
}
</style>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { MapLayerMouseEvent } from "maplibre-gl";
import { MglMap, useMap, type MglEvent } from "vue-maplibre-gl";
import FullLayout from "@/components/layout/Full.vue";
import ToolRail from "@/components/panel/ToolRail.vue";
import MapvisionHelpModal from "@/components/MapvisionHelpModal.vue";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { boundsFromGeoJSON, mapStyleURL } from "@/map/util";
import SourceServiceArea from "@/map/SourceServiceArea.vue";
import SourceMapvisionZone from "@/map/SourceMapvisionZone.vue";
import SourceMapvisionSites from "@/map/SourceMapvisionSites.vue";
import SourceParcel from "@/map/SourceParcel.vue";
import { MAPVISION_SITE_TYPE_SYMBOLS } from "@/lib/mapvision";
import { rawSitusRows } from "@/lib/situs";
import { apiClient } from "@/client";
import type {
	MapvisionSite,
	MapvisionSiteSearchResult,
	MunicipalParcel,
	MunicipalSitusDetail,
} from "@/type/api";
import type { GeoJSON } from "geojson";

const mapInstance = useMap();

const helpOpen = ref(false);
const leftTab = ref<string | null>(null);
const selectedSite = ref<MapvisionSite | null>(null);
// Nearest municipal parcel (from site.parcel_uri), for filling in address data
// MapVision may have missed.
const parcel = ref<MunicipalParcel | null>(null);
// Situs (assessment) row linked to the parcel (from parcel.situs_uri).
const situs = ref<MunicipalSitusDetail | null>(null);

// The source's unmapped assessor fields (raw_data); shown after the typed rows
// so the panel carries the whole record, not just the mapped columns.
const situsRawRows = computed(() => rawSitusRows(situs.value?.raw_data));

async function setSelectedSite(site: MapvisionSite) {
	selectedSite.value = site;
	leftTab.value = "site";
	if (site.parcel_uri) {
		try {
			parcel.value = await apiClient.JSONGet<MunicipalParcel>(site.parcel_uri);
		} catch {
			parcel.value = null;
		}
	} else {
		parcel.value = null;
	}
	if (parcel.value?.situs_uri) {
		try {
			situs.value = await apiClient.JSONGet<MunicipalSitusDetail>(
				parcel.value.situs_uri,
			);
		} catch {
			situs.value = null;
		}
	} else {
		situs.value = null;
	}
}

// Site search (autocomplete) in the Site panel.
const searchQuery = ref("");
const searchResults = ref<MapvisionSiteSearchResult[]>([]);
const searchOpen = ref(false);
let searchTimer: ReturnType<typeof setTimeout> | undefined;

function onSearchInput() {
	clearTimeout(searchTimer);
	const q = searchQuery.value.trim();
	if (q.length < 2) {
		searchResults.value = [];
		searchOpen.value = false;
		return;
	}
	searchTimer = setTimeout(async () => {
		try {
			searchResults.value = await apiClient.JSONGet<
				MapvisionSiteSearchResult[]
			>(`/api/mapvision/site?q=${encodeURIComponent(q)}`);
			searchOpen.value = searchResults.value.length > 0;
		} catch {
			searchResults.value = [];
			searchOpen.value = false;
		}
	}, 300);
}

function onSearchBlur() {
	// Give a mousedown on a result time to register before hiding the list.
	setTimeout(() => {
		searchOpen.value = false;
	}, 150);
}

async function selectSearchResult(r: MapvisionSiteSearchResult) {
	searchOpen.value = false;
	searchResults.value = [];
	searchQuery.value = "";
	try {
		// Load the full site (with history) via the same endpoint the map
		// uses. /api/mapvision/site/{id} keys on the mapvision.sites
		// surrogate id, not i_site_id — use r.id here.
		const site = await apiClient.JSONGet<MapvisionSite>(
			`/api/mapvision/site/${r.id}`,
		);
		await setSelectedSite(site);
	} catch {
		// Leave the panel as-is if the site detail can't be fetched.
	}
}

const rightTab = ref<string | null>("site-types");
// Site types currently hidden from the map. Empty = all types shown.
const hiddenTypes = ref<Set<string>>(new Set());
const typeRows = computed(() => Object.entries(MAPVISION_SITE_TYPE_SYMBOLS));

function toggleType(type: string) {
	const next = new Set(hiddenTypes.value);
	if (next.has(type)) next.delete(type);
	else next.add(type);
	hiddenTypes.value = next;
}

const siteTitle = computed(() => {
	const s = selectedSite.value;
	if (!s) return "";
	return s.name || s.type_name || `Site ${s.site_id}`;
});

function formatDate(d: string): string {
	return d.slice(0, 10);
}

// fmtDate is formatDate tolerant of null/empty history timestamps.
function fmtDate(d: string | null): string {
	return d ? d.slice(0, 10) : "";
}

// fmtDeleted renders the placer isDeleted marker (varchar: "0"/"1"/"Y"/"N")
// as a readable Yes/—.
function fmtDeleted(v: string | null): string {
	if (v == null) return "";
	const s = v.trim().toLowerCase();
	return s === "1" || s === "y" || s === "true" ? "Yes" : "—";
}

// fmtNumber renders a number with thousands separators (integer when whole).
function fmtNumber(v: number): string {
	return v.toLocaleString("en-US", {
		maximumFractionDigits: 2,
	});
}

// fmtCurrency renders a value as US dollars.
function fmtCurrency(v: number): string {
	return v.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	});
}

const siteHistory = computed(() => selectedSite.value?.history ?? []);

const siteRows = computed<Array<[string, string]>>(() => {
	const s = selectedSite.value;
	if (!s) return [];
	const rows: Array<[string, string]> = [];
	if (s.type_name) rows.push(["Type", s.type_name]);
	if (s.sub_type_name) rows.push(["Sub-type", s.sub_type_name]);
	if (s.zone_name) rows.push(["Zone", s.zone_name]);
	const address = [s.address1, s.street].filter(Boolean).join(" ");
	if (address) rows.push(["Address", address]);
	const cityState = [s.city, s.state].filter(Boolean).join(", ");
	if (cityState) rows.push(["City / State", cityState]);
	if (s.zipcode) rows.push(["ZIP", s.zipcode]);
	if (s.county) rows.push(["County", s.county]);
	if (s.calsurv_sitecode) rows.push(["Calsurv code", s.calsurv_sitecode]);
	if (s.xref) rows.push(["Xref", s.xref]);
	if (s.no_spray != null) rows.push(["No spray", s.no_spray ? "Yes" : "No"]);
	if (s.notify != null) rows.push(["Notify", s.notify ? "Yes" : "No"]);
	if (s.reinspection_date)
		rows.push(["Reinspection", formatDate(s.reinspection_date)]);
	if (s.date_last_action)
		rows.push(["Last action", formatDate(s.date_last_action)]);
	if (s.date_last_treated)
		rows.push(["Last treated", formatDate(s.date_last_treated)]);
	return rows;
});

function onServiceAreaLoaded(serviceAreaGeometry: GeoJSON.GeoJSON | null) {
	// Zoom the map to the organization's service area so the review starts
	// centered on the data that was synchronized from MapVision.
	if (serviceAreaGeometry) {
		const bounds = boundsFromGeoJSON(serviceAreaGeometry);
		if (bounds) {
			mapInstance.map?.fitBounds(bounds, { padding: 40, duration: 800 });
		}
	}
}

function onMapLoad(event: MglEvent<"load">) {
	const m = event.map;
	m.on("click", "mapvision-site-dots", async (ev: MapLayerMouseEvent) => {
		// Tegola uses `id` as the MVT feature id, so it may live on
		// feature.id rather than in properties — read both.
		const feature = ev.features?.[0];
		const id = (feature?.id ?? feature?.properties?.id) as number | undefined;
		if (id == null) return;
		try {
			const site = await apiClient.JSONGet<MapvisionSite>(
				`/api/mapvision/site/${id}`,
			);
			await setSelectedSite(site);
		} catch {
			// Leave the panel closed if the site detail can't be fetched.
		}
	});
	m.on("mouseenter", "mapvision-site-dots", () => {
		m.getCanvas().style.cursor = "pointer";
	});
	m.on("mouseleave", "mapvision-site-dots", () => {
		m.getCanvas().style.cursor = "";
	});
}
</script>
